import { Component, Input } from '@angular/core';
import { ProductLookupService } from './product-lookup.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { debounceTime, Subject } from 'rxjs';
import * as XLSX from 'xlsx';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [NgFor, NgIf, CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './product-lookup.component.html',
  styleUrl: './product-lookup.component.scss'
})
export class ProductLookupComponent {

  form: FormGroup;
  products: any[] = [];
  data: any[] = [];
  allResults: any[] = [];
  filteredProducts: any[] = [];
  batchList: any[] = [];
  expiredDates: string[] = [];
  isLoading = false;
  showSearchResult = false;
  result: any = null;

  private readonly debounceTimeMs = 300;
  searchSubject: Subject<any> = new Subject<any>();

  constructor(
    private productLookupService: ProductLookupService,
    private fb: FormBuilder,
    private toastService: ToastrService,
  ) {
    this.form = this.fb.group({
      productName: ['', Validators.required],
      batch: ['', Validators.required],
      expiredDate: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(this.debounceTimeMs)).subscribe((searchValue) => {
      this.searchProduct(searchValue);
    });

    this.loadAllPurchaseOrders();
  }


  loadAllPurchaseOrders() {
    this.productLookupService.searchPurchaseOrder({}).subscribe((res: any) => {
      if (!res?.data) {
        this.toastService.error('Không tìm thấy dữ liệu đơn nhập hàng!');
        return;
      }

      const grouped = new Map<string, Map<string, Set<string>>>(); // Map<ProductName, Map<Batch, Set<ExpiredDate>>>

      this.allResults = res.data.map((item: any) => {
        let productName = '';
        try {
          const parsedName = JSON.parse(item.productName);
          productName = parsedName?.vi_VN || parsedName?.en_US || '';
        } catch {
          productName = item.productName || '';
        }

        // Build map
        if (!grouped.has(productName)) grouped.set(productName, new Map());
        const batchMap = grouped.get(productName)!;
        if (!batchMap.has(item.batch)) batchMap.set(item.batch, new Set());
        batchMap.get(item.batch)!.add(item.expiredDate.split('T')[0]);

        return {
          ...item,
          ProductName: productName,
          Batch: item.batch,
          ExpiredDate: item.expiredDate.split('T')[0],
        };
      });

      // Convert grouped map to this.products
      this.products = Array.from(grouped.entries()).map(([productName, batchMap]) => ({
        name: productName,
        batches: Array.from(batchMap.entries()).map(([batch, dateSet]) => ({
          name: batch,
          expiredDates: Array.from(dateSet)
        }))
      }));
    });
  }

  // Gọi API tìm sản phẩm theo tên (debounced)
  searchProduct(searchTerm: string) {
    const body = {
      searchTerm,
      batch: '',
      expiredDate: '',
      limit: 200
    };

    // Gửi API để tìm sản phẩm theo tên
    this.productLookupService.searchPurchaseOrder(body).subscribe((res: any) => {
      // Không có kết quả từ server => hiển thị danh sách rỗng
      if (!res.data?.length) {
        this.filteredProducts = [];
        this.showSearchResult = true;
        return;
      }

      const grouped = new Map<string, Set<string>>();

      res.data.forEach((po: any) => {
        let name = '';
        try {
          name = typeof po.productName === 'string'
            ? JSON.parse(po.productName)?.vi_VN || po.productName
            : po.productName?.vi_VN || po.productName?.en_US || '';
        } catch {
          name = po.productName;
        }

        if (!grouped.has(name)) grouped.set(name, new Set());
        grouped.get(name)!.add(po.batch);
      });

      // Chuyển map thành danh sách để hiển thị dropdown
      this.filteredProducts = Array.from(grouped.keys()).map(name => ({ name }));

      this.allResults = res.data;
      this.showSearchResult = true;
    });
  }



  onInput(event: any) {
    const input = event.target.value;

    if (!input) {
      this.filteredProducts = [];
      this.showSearchResult = false;
      return;
    }

    this.searchSubject.next(input);
  }

  selectProduct(product: any) {
    const productName = product.name;

    this.form.patchValue({ productName, batch: '', expiredDate: '' });
    this.showSearchResult = false;
    this.batchList = [];
    this.expiredDates = [];

    this.productLookupService.searchPurchaseOrder({
      searchTerm: productName,
      batch: '',
      expiredDate: '',
      limit: 200
    }).subscribe((res: any) => {
      if (!res.data?.length) {
        this.toastService.warning('Không tìm thấy lô nào cho sản phẩm này.');
        return;
      }

      const batchMap = new Map<string, Set<string>>();
      res.data.forEach((po: any) => {
        if (!po.batch || !po.expiredDate) return;
        const date = po.expiredDate.split('T')[0];
        if (!batchMap.has(po.batch)) batchMap.set(po.batch, new Set());
        batchMap.get(po.batch)!.add(date);
      });

      this.batchList = Array.from(batchMap.entries()).map(([batch, dates]) => ({
        name: batch,
        expiredDates: Array.from(dates)
      }));

      if (this.batchList.length === 1) {
        const only = this.batchList[0];
        this.expiredDates = only.expiredDates;
        this.form.patchValue({
          batch: only.name,
          expiredDate: only.expiredDates[0]
        });
      }
    }, () => {
      this.toastService.error('Lỗi khi tải lô của sản phẩm.');
    });
  }


  onSelectBatch(event: any) {
    const batch = this.batchList.find(b => b.name === event.target.value);
    this.expiredDates = batch?.expiredDates || [];

    this.form.patchValue({
      expiredDate: ''
    });
  }

  selectBatchItem(batchname: any) {
    let batch = this.batchList.find((batch) => batch.name === batchname);
    if (batch) {
      this.expiredDates = batch.expiredDates;
      this.form.patchValue({
        batch: batch.name,
        expiredDate: this.expiredDates[0]
      });
    }
    else {
      this.form.patchValue({
        batch: batch.name,
        expiredDate: this.expiredDates[0]
      });
    }
    this.onSubmit();
  }

  onSubmit() {
    this.result = null;

    if (this.form.invalid) {
      this.toastService.error('Xin nhập đầy đủ thông tin!');
      return;
    }

    this.isLoading = true;

    const { productName, batch, expiredDate } = this.form.value;

    this.productLookupService.searchPurchaseOrder({
      searchTerm: productName,
      batch: batch,
      expiredDate: expiredDate,
      limit: 1
    }).subscribe({
      next: (res: any) => {
        const list = res?.data ?? [];

        if (!list.length) {
          this.toastService.warning('Không tìm thấy kết quả phù hợp.');
          this.result = null;
        } else {
          const po = list[0];

          this.result = {
            ...po,
            ProductName: typeof po.productName === 'string'
              ? (JSON.parse(po.productName)?.vi_VN ?? po.productName)
              : po.productName?.vi_VN ?? po.productName?.en_US ?? '',
            Batch: po.batch,
            ExpiredDate: po.expiredDate?.split('T')[0] ?? ''
          };
        }

        this.isLoading = false;
      },
      error: () => {
        this.toastService.error('Lỗi khi tra cứu sản phẩm.');
        this.isLoading = false;
      }
    });
  }



  getDate(inputDate: string) {
    let date = inputDate.split('/');
    return date[2] + '-' + date[0].padStart(2, '0') + '-' + date[1].padStart(2, '0');
  }
}
